import { nullableArray, nullableOptional, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

export enum TailoredSupplyStatus {
  Active = 'ACTIVE',
}

export enum TailoredSupplyEditorTab {
  General = 'general',
  Apps = 'apps',
  Domains = 'domains',
}

export enum TailoredSupplySortProperty {
  CreatedOn = 'createdOn',
  Name = 'name',
}

export enum TailoredSupplySortDirection {
  Ascending = 'ASC',
  Descending = 'DESC',
}

export enum TailoredSupplyLanguage {
  English = 'en',
  Spanish = 'es',
}

const identifierSchema = z.union([z.string(), z.number()]).transform(String);
const nullableString = nullableOptional(z.string());
const stringListSchema = z
  .union([z.string(), nullableArray(z.string())])
  .nullish()
  .transform(normalizeStringList);

function normalizeStringList(value: string | string[] | null | undefined) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export const tailoredSupplyReferenceSchema = z
  .union([
    identifierSchema,
    z.looseObject({
      id: nullableOptional(identifierSchema),
      code: nullableString,
      key: nullableString,
      name: nullableString,
      title: nullableString,
    }),
  ])
  .transform((reference) => {
    if (typeof reference === 'string') return { id: reference, name: reference };
    const id = reference.id ?? reference.code ?? reference.key ?? reference.name ?? '';
    return {
      id,
      name: reference.name ?? reference.title ?? reference.code ?? reference.key ?? id,
      ...(reference.code ? { code: reference.code } : {}),
    };
  });

export function resolveTailoredSupplyReferenceNames(
  values: string[],
  references: TailoredSupplyReference[],
) {
  const namesByReference = new Map<string, string>();
  references.forEach((reference) => {
    [reference.id, reference.code, reference.name].forEach((value) => {
      if (value) namesByReference.set(value.trim().toLowerCase(), reference.name);
    });
  });
  return values.map((value) => namesByReference.get(value.trim().toLowerCase()) ?? value);
}

export const tailoredSupplyReferenceListSchema = z
  .union([
    z.array(tailoredSupplyReferenceSchema),
    z.looseObject({ content: nullableArray(tailoredSupplyReferenceSchema) }),
  ])
  .transform((value) => (Array.isArray(value) ? value : value.content));

export const tailoredSupplyItemSchema = z
  .looseObject({
    id: identifierSchema,
    name: nullableString,
    description: nullableString,
    appsCount: z.number().nullish(),
    domainsCount: z.number().nullish(),
    publishers: stringListSchema,
    appPlatforms: stringListSchema,
    appIabCategories: stringListSchema,
    appLanguages: stringListSchema,
  })
  .transform((item) => ({
    ...item,
    name: item.name ?? item.id,
    description: item.description ?? undefined,
    appsCount: item.appsCount ?? 0,
    domainsCount: item.domainsCount ?? 0,
  }));

export const tailoredSupplyAppSchema = z
  .looseObject({
    id: identifierSchema,
    name: nullableString,
    publisherNames: stringListSchema,
    bundleId: nullableString,
    platform: tailoredSupplyReferenceSchema.nullish(),
  })
  .transform((app) => ({
    ...app,
    name: app.name ?? app.bundleId ?? app.id,
    bundleId: app.bundleId ?? undefined,
    platform: app.platform ?? undefined,
  }));

export const tailoredSupplyDomainSchema = z
  .looseObject({
    id: identifierSchema,
    url: nullableString,
    publisher: tailoredSupplyReferenceSchema.nullish(),
  })
  .transform((domain) => ({
    ...domain,
    url: domain.url ?? domain.id,
    publisher: domain.publisher ?? undefined,
  }));

export const tailoredSupplyPageSchema = <TSchema extends z.ZodType>(schema: TSchema) =>
  z
    .looseObject({
      content: nullableArray(schema),
      totalElements: z.number().nullish(),
    })
    .transform((page) => ({
      content: page.content,
      totalElements: page.totalElements ?? page.content.length,
    }));

export const tailoredSupplyMutationResponseSchema = z.preprocess(
  (value) => (value === '' ? null : value),
  z.union([z.null(), z.void(), z.looseObject({})]),
);

export type TailoredSupplyItem = z.infer<typeof tailoredSupplyItemSchema>;
export type TailoredSupplyApp = z.infer<typeof tailoredSupplyAppSchema>;
export type TailoredSupplyDomain = z.infer<typeof tailoredSupplyDomainSchema>;
export type TailoredSupplyReference = z.infer<typeof tailoredSupplyReferenceSchema>;

interface TailoredSupplyPagedFilter {
  page: number;
  size: number;
}

export interface TailoredSupplyListFilter extends TailoredSupplyPagedFilter {
  name: string;
  sortProperty: TailoredSupplySortProperty | null;
  sortDirection: TailoredSupplySortDirection;
}

export interface TailoredSupplyLookupFilter extends TailoredSupplyPagedFilter {
  searchTerm: string;
  status: TailoredSupplyStatus;
  sortProperty: TailoredSupplySortProperty | null;
  sortDirection: TailoredSupplySortDirection;
}

export interface TailoredSupplyAppsFilter extends TailoredSupplyPagedFilter {
  searchTerm: string;
  publisherIds: string[];
  platformIds: string[];
  languages: TailoredSupplyLanguage[];
  iabCategoryIds: string[];
  sortProperty: TailoredSupplySortProperty | null;
  sortDirection: TailoredSupplySortDirection;
}

export interface TailoredSupplyDomainsFilter extends TailoredSupplyPagedFilter {
  searchTerm: string;
  publisherIds: string[];
  iabCategoryIds: string[];
  sortProperty: TailoredSupplySortProperty | null;
  sortDirection: TailoredSupplySortDirection;
}

export interface TailoredSupplyCreatePayload {
  name: string;
  description: string | null;
  appIds: string[];
  domainIds: string[];
}

export const tailoredSupplyPageSizes = [5, 10, 20, 25, 50, 100] as const;

export const defaultTailoredSupplyListFilter: TailoredSupplyListFilter = {
  name: '',
  page: 0,
  size: 20,
  sortProperty: TailoredSupplySortProperty.CreatedOn,
  sortDirection: TailoredSupplySortDirection.Descending,
};

export const defaultTailoredSupplyLookupFilter: TailoredSupplyLookupFilter = {
  page: 0,
  size: 5,
  searchTerm: '',
  status: TailoredSupplyStatus.Active,
  sortProperty: TailoredSupplySortProperty.Name,
  sortDirection: TailoredSupplySortDirection.Ascending,
};

export const defaultTailoredSupplyAppsFilter: TailoredSupplyAppsFilter = {
  searchTerm: '',
  publisherIds: [],
  platformIds: [],
  languages: [],
  iabCategoryIds: [],
  page: 0,
  size: 10,
  sortProperty: TailoredSupplySortProperty.Name,
  sortDirection: TailoredSupplySortDirection.Ascending,
};

export const defaultTailoredSupplyDomainsFilter: TailoredSupplyDomainsFilter = {
  searchTerm: '',
  publisherIds: [],
  iabCategoryIds: [],
  page: 0,
  size: 10,
  sortProperty: TailoredSupplySortProperty.Name,
  sortDirection: TailoredSupplySortDirection.Ascending,
};

export function serializeTailoredSupplyListFilter(filter: TailoredSupplyListFilter) {
  return toSearchParams({
    name: filter.name,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  });
}

export function serializeTailoredSupplyLookupFilter(filter: TailoredSupplyLookupFilter) {
  return toSearchParams({
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
    searchTerm: filter.searchTerm.trim(),
    status: filter.status,
  });
}

export function serializeTailoredSupplyAppsFilter(filter: TailoredSupplyAppsFilter) {
  return toSearchParams({
    searchTerm: filter.searchTerm,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
    full: true,
    statuses: [TailoredSupplyStatus.Active],
    publisherIds: filter.publisherIds,
    platformIds: filter.platformIds,
    languages: filter.languages,
    iabCategoriesIds: filter.iabCategoryIds,
  });
}

export function serializeTailoredSupplyDomainsFilter(filter: TailoredSupplyDomainsFilter) {
  return toSearchParams({
    page: filter.page,
    name: filter.searchTerm,
    size: filter.size,
    full: true,
    statuses: [TailoredSupplyStatus.Active],
    publisherIds: filter.publisherIds.length ? filter.publisherIds : undefined,
    iabCategoriesIds: filter.iabCategoryIds.length ? filter.iabCategoryIds : undefined,
  });
}

export function isTailoredSupplySortProperty(value: string): value is TailoredSupplySortProperty {
  return Object.values(TailoredSupplySortProperty).includes(value as TailoredSupplySortProperty);
}

export function isTailoredSupplyEditorTab(value: string): value is TailoredSupplyEditorTab {
  return Object.values(TailoredSupplyEditorTab).includes(value as TailoredSupplyEditorTab);
}
